import Asset, {AssetCategory} from "../../../model/Asset";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormLabel,
  Paper,
  Typography,
  useTheme
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {
  getISODateFromDate,
  moneyToNumberFormat,
  amountToEditText,
  editTextToAmount,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import FormValidator from "../../../components/form/FormValidator";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import SnapshotIcon from '@mui/icons-material/CameraAlt';
import IconicButton from "../../../components/controls/IconicButton";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import DialogButton from "../../../components/form/DialogButton";
import Snapshot, {ISnapshotDetail, SnapshotDetail} from "../../../model/Snapshot";
import {format, startOfToday} from "date-fns";
import Liability from "../../../model/Liability";
import {CreateSnapshotInput, SnapshotType, UpdateSnapshotInput} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import {LiabilityIcon, LiquidAssetIcon, OtherAssetIcon, PersonalAssetIcon, RealAssetIcon} from "../../../styles/Icons";
import INetWorth from "../../../model/INetWorth";
import {globalColors} from "../../../styles/globalStyles";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {isNumber} from "../../../model/ModelUtilities";
import Schedule from "../../../model/Schedule";

export class SnapshotEditDetail {
  id: string
  typename: string
  balance: string
  amount?: string
  originalAmount?: number
  schedule?: Schedule

  constructor (item: ISnapshotDetail) {
    this.id = item.id
    this.typename = item.typename
    this.balance = numberToMoneyFormat(item.balance, 0)
    this.amount = isNumber(item.amount) ? amountToEditText(item.amount!) : undefined
    this.originalAmount = isNumber(item.amount) ? item.amount : undefined
    this.schedule = item.schedule
  }
}

class Total {
  title: string
  count: number
  baseBalance: number
  newBalance: number
  changePct: number

  constructor () {
    this.title = ""
    this.count = 0
    this.baseBalance = 0
    this.newBalance = 0
    this.changePct = 0
  }
}

const NetWorthSnapshotDialog = ({
  baseSnap,
  snapshot,
  compare,
  liquidAssets,
  realAssets,
  personalAssets,
  otherAssets,
  liabilities,
  onCancel,
  onSave,
  onDelete
}: {
  baseSnap?: Snapshot
  snapshot: Snapshot
  compare?: boolean
  liquidAssets: Asset[]
  realAssets: Asset[]
  personalAssets: Asset[]
  otherAssets: Asset[]
  liabilities: Liability[]

  onCancel?: () => any
  onSave?: (snapshot: Snapshot) => any
  onDelete?: (snapshot: Snapshot) => any
  disableDelete?: boolean
}) => {
  const [isNew, setIsNew] = useState<boolean>(false)
  const [isComparing, setIsComparing] = useState<boolean>(false)
  const [canDelete, setCanDelete] = useState<boolean>(false)
  const [snapshotDate, setSnapshotDate] = useState<Date | undefined>()
  const [snapshotDescription, setSnapshotDescription] = useState<string>("")
  const [snapshotEditItems, setSnapshotEditItems] = useState<SnapshotEditDetail[]>([])
  const [snapshotType, setSnapshotType] = useState<SnapshotType>(SnapshotType.NetWorth)

  const { modelStore, confirm, notify } = useResources()

  useEffect(() => {
    loadSnapshot(snapshot)
  }, [snapshot])

  const loadSnapshot = (snapshot: Snapshot) => {
    setIsNew(!snapshot.id || snapshot.id === "current")
    setCanDelete(!compare && (!snapshot.id || snapshot.id !== "current"))
    setSnapshotDate(snapshot.date)
    setSnapshotDescription(snapshot.description ?? "")
    const editItems = snapshot.details.map((item: ISnapshotDetail) => new SnapshotEditDetail(item))
    setSnapshotEditItems(editItems)
    setSnapshotType(snapshot.snapshotType)
    if (compare) {
      setIsComparing(true)
    }
  }

  const theme = useTheme()

  const styles = {
    snapshotIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    actions: {
      display:"flex",
      flexDirection:"row",
      width:"100%",
      justifyContent:"space-between",
      alignItems:"center"
    },
    itemBox: {
      display:"flex",
      flexGrow:1,
      alignItems:"center",
      width: "100%",
      maxHeight:40,
      backgroundColor: theme.palette.grey["50"],
      borderWidth: 1,
      borderColor: theme.palette.background.default,
      borderStyle: "solid",
      borderRadius: 0,
      padding: 0,
      marginBottom: 1
    },
    accountIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#40642F",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    realEstateIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#7f5539",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    personalPropertyIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#b08968",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    otherPropertyIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#ddb892",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    liabilityIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#881E10",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      paddingTop: "4px",
      color: theme.palette.grey["700"]
    },
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  const handleSave = async () => {
    if (!snapshotDate) {
      return
    }
    const details = snapshotEditItems.map((item: SnapshotEditDetail) => {
      return new SnapshotDetail({id: item.id, typename: item.typename, balance: moneyToNumberFormat(item.balance, 0), amount: editTextToAmount(item.amount, item.originalAmount), schedule: item.schedule})
    })
    let update
    // Verify there isn't already a snapshot for this date.
    const isoDate = getISODateFromDate(snapshotDate!)
    if (isNew || snapshot.date !== snapshotDate) {
      const existing = await modelStore.getSnapshotByDate(snapshot.modelId, isoDate)
      if (existing) {
        if (existing.snapshotType === SnapshotType.Budget) {
          // Allow merge with a Bydget snapshot
          await mergeSnapshot(existing, details)
          return
        } else {
          notify.show('error', `A snapshot already exists for ${format(snapshotDate, "MM/dd/yyyy")}`)
          return
        }
      }
    }

    if (isNew) {
      const input: CreateSnapshotInput = {
        accountId: snapshot.accountId,
        userId: snapshot.userId,
        modelId: snapshot.modelId,
        date: isoDate,
        description: snapshotDescription,
        snapshotType: snapshotType,
        details: JSON.stringify(details)
      }

      update = await modelStore.createSnapshot(input)
    } else {
      const input: UpdateSnapshotInput = {
        id: snapshot.id,
        accountId: snapshot.accountId,
        userId: snapshot.userId,
        modelId: snapshot.modelId,
        date: getISODateFromDate(snapshotDate!),
        description: snapshotDescription,
        details: JSON.stringify(details)
      }

      update = await modelStore.updateSnapshot(input)
    }

    if (update && onSave) {
      onSave(update)
    }
  }

  const mergeSnapshot = async (existing: Snapshot, details: SnapshotDetail[]) => {
    const mergedDetails = [...existing.details, ...details]
    const input: UpdateSnapshotInput = {
      id: existing.id,
      accountId: existing.accountId,
      userId: existing.userId,
      modelId: existing.modelId,
      description: snapshotDescription,
      snapshotType: SnapshotType.Combined,
      details: JSON.stringify(mergedDetails)
    }

    const update = await modelStore.updateSnapshot(input)

    if (update && onSave) {
      onSave(update)
    }
  }

  const handleDelete = async () => {
    if (onDelete) {
      confirm.show('Confirm Delete', 'Are you sure you want to delete this snapshot?',
        ['Delete', 'Cancel'],
        async () => {
          try {
            const deleted = await modelStore.deleteSnapshot(snapshot.id)
            if (deleted) {
              onDelete(deleted)
              return true
            }
            return false
          }
          catch (err: any) {
            notify.show('error', err.message)
            return false
          }
        })
    }
  }

  // const netWorthFilterFn = (a: INetWorth): boolean => {
  //   // Not currently in use
  //   if (!snapshotDate) {
  //     return false
  //   }
  //   const startDate = a.startDate
  //   const endDate = a.endDate
  //   return ((!startDate || (snapshotDate.getTime() >= startDate.getTime())) &&
  //           (!endDate   || (snapshotDate.getTime() <= endDate.getTime())))
  // }

  const title = isNew ? "New Snapshot" : isComparing ? "Compare Snapshots" : "Edit Snapshot"

  const renderCategory = (title: string, subtotal?: Total) => {
    if (subtotal) {
      subtotal.title = title
      subtotal.count = 0
      subtotal.baseBalance = 0
      subtotal.newBalance = 0
      subtotal.changePct = 0
    }

    return (
      <Box display="flex" flexGrow={1} justifyContent="space-between">
        <Box display="flex" flexGrow={1}>
          <Typography variant="h4" color="primary" sx={{fontSize: 18}}>{title}</Typography>
        </Box>
        {baseSnap &&
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <FormLabel sx={styles.formLabel}>Prior Balance</FormLabel>
          </Box>
        }
        <Box display="flex" flexGrow={0} width={130} mr={2}>
          <FormLabel sx={styles.formLabel}>New Balance</FormLabel>
        </Box>
        <Box display="flex" flexGrow={0} width={65}>
          <FormLabel sx={styles.formLabel}>Change %</FormLabel>
        </Box>
      </Box>
    )
  }

  const renderItem = (item: INetWorth, subtotal: Total, total: Total, excludedTotal?: Total) => {
    let icon
    if (item instanceof Asset) {
      switch (item.assetCategory) {
        case AssetCategory.LiquidInvestableAssets:
          icon = <LiquidAssetIcon managed={item.managed}/>
          break
        case AssetCategory.RealEstateAndProperty:
          icon = <RealAssetIcon/>
          break
        case AssetCategory.PersonalProperty:
          icon = <PersonalAssetIcon/>
          break
        case AssetCategory.OtherAssets:
          icon = <OtherAssetIcon managed={item.managed}/>
          break
        default:
          icon = <LiquidAssetIcon/>
          break
      }
    } else {
      icon = <LiabilityIcon/>
    }

    const typename = item instanceof Asset ? "Asset" : item instanceof Liability ? "Liability" : ""
    const isExcluded = item instanceof Asset && item.netWorthExcluded === true
    let editIndex = snapshotEditItems.findIndex((detail: SnapshotEditDetail) => detail.id === item.id && detail.typename === typename)
    let balance = ""
    if (editIndex >= 0) {
      balance = snapshotEditItems[editIndex].balance
    } else if ((item.startDate && (snapshotDate && snapshotDate.getTime() < item.startDate.getTime())) || item.archived) {
      // Don't display item if the snapshot date is before the item startDate or archived
      return null
    }

    let baseBalance= 0
    if (baseSnap) {
      baseBalance = baseSnap.getSnapshotBalance(item.id, typename)
    }

    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseBalance > 0) {
      changePct = numberToPercentFormat((moneyToNumberFormat(balance) - baseBalance) / baseBalance, 2)
    } else if (!balance) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.income
    }

    const newBalance = moneyToNumberFormat(balance, 0)
    if (isExcluded && excludedTotal) {
      excludedTotal.count += 1
      excludedTotal.baseBalance += baseBalance
      excludedTotal.newBalance += newBalance
    } else {
      subtotal.count += 1
      subtotal.baseBalance += baseBalance
      subtotal.newBalance += newBalance
      total.count += 1
      total.baseBalance += baseBalance
      total.newBalance += newBalance
    }

    return (
      <Box display="flex" flexGrow={1} justifyContent="stretch" key={item.id} sx={isExcluded ? {opacity: 0.55} : undefined}>
        <Box sx={styles.itemBox}>
          <Box display="flex" flexGrow={0} width={40}>
            {icon}
          </Box>
          <Box display="flex" flexGrow={1} pr={2}>
            <Typography variant="body1">{item.description}{isExcluded ? "*" : ""}</Typography>
          </Box>
          {baseSnap &&
            <Box display="flex" flexGrow={0} width={130} mr={2}>
              <TextFieldValidator
                margin="none"
                name={`base-${item.id}`}
                label=" "
                type="text"
                fullWidth
                variant="standard"
                value={numberToMoneyFormat(baseBalance, 0)}
                disabled={true}
                InputLabelProps={{shrink: false}}
              />
            </Box>
          }
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <TextFieldValidator
              margin="none"
              name={item.id}
              label=" "
              type="text"
              fullWidth
              variant="standard"
              required
              validators={{ required: false, isMoney: true }}
              value={balance}
              InputLabelProps={{shrink: false}}
              onChange={(event: any) => {
                if (editIndex < 0) {
                  const typename = (item instanceof Asset) ? "Asset" : "Liability"
                  const editItem = new SnapshotEditDetail({id: item.id, typename: typename, balance: 0})
                  snapshotEditItems.push(editItem)
                  editIndex = snapshotEditItems.length - 1
                }
                snapshotEditItems[editIndex].balance = event.currentTarget.value
                setSnapshotEditItems([...snapshotEditItems])
              }}
            />
          </Box>
          <Box display="flex" flexGrow={0} width={65} color={changeColor} pt={1.5} whiteSpace="nowrap">
            {changeIcon} <Typography variant="body2" color={changeColor}>{changePct}</Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderSubtotal = (subtotal: Total) => {
    if (subtotal.count <= 0) {
      return null
    }

    let baseBalance = subtotal.baseBalance
    let balance = subtotal.newBalance
    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseBalance > 0) {
      const change = (balance - baseBalance) / baseBalance
      changePct = numberToPercentFormat(change, change > -1 && change < 1 ? 2 : 0)
    } else if (!balance) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.income
    }


    return (
      <Box display="flex" flexGrow={1} justifyContent="space-between" mb={1}>
        <Box display="flex" flexGrow={0} width={40}>
        </Box>
        <Box display="flex" flexGrow={1}>
          <Typography variant="h5">Subtotals</Typography>
        </Box>
        {baseSnap &&
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <Typography variant="h5">{numberToMoneyFormat(subtotal.baseBalance, 0)}</Typography>
          </Box>
        }
        <Box display="flex" flexGrow={0} width={130} mr={2}>
          <Typography variant="h5">{numberToMoneyFormat(subtotal.newBalance, 0)}</Typography>
        </Box>
        <Box display="flex" flexGrow={0} width={65} color={changeColor} whiteSpace="nowrap">
          {changeIcon} <Typography variant="h5" color={changeColor}>{changePct}</Typography>
        </Box>
      </Box>
    )
  }

  const renderTotal = (total: Total) => {
    let baseBalance = total.baseBalance
    let balance = total.newBalance
    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseBalance > 0) {
      const change = (balance - baseBalance) / baseBalance
      changePct = numberToPercentFormat(change, change > -1 && change < 1 ? 2 : 0)
    } else if (!balance) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.income
    }


    return (
      <Box display="flex" flexGrow={1} justifyContent="space-between" mb={1}>
        <Box display="flex" flexGrow={0} width={40}>
        </Box>
        <Box display="flex" flexGrow={1}>
          <Typography variant="h5">{total.title}</Typography>
        </Box>
        {baseSnap &&
          <Box display="flex" flexGrow={0} width={130} mr={2}>
            <Typography variant="h5">{numberToMoneyFormat(total.baseBalance, 0)}</Typography>
          </Box>
        }
        <Box display="flex" flexGrow={0} width={130} mr={2}>
          <Typography variant="h5">{numberToMoneyFormat(total.newBalance, 0)}</Typography>
        </Box>
        <Box display="flex" flexGrow={0} width={65} color={changeColor} whiteSpace="nowrap">
          {changeIcon} <Typography variant="h5" color={changeColor}>{changePct}</Typography>
        </Box>
      </Box>
    )
  }

  const renderTotals = (totals: Total[]) => {
    const net = totals[2]
    net.baseBalance = totals[0].baseBalance - totals[1].baseBalance
    net.newBalance = totals[0].newBalance - totals[1].newBalance
    return totals
      .filter((t: Total, i: number) => i !== 3 || t.count > 0)
      .map((total: Total) => renderTotal(total))
  }

  const maxDate = startOfToday()
  let subtotal: Total = new Total()
  let totals: Total[] = [
    {title: "Assets", count: 0, baseBalance: 0, newBalance: 0, changePct: 0},
    {title: "Liabilities", count: 0, baseBalance: 0, newBalance: 0, changePct: 0},
    {title: "Net Worth", count: 0, baseBalance: 0, newBalance: 0, changePct: 0},
    {title: "Excluded Assets", count: 0, baseBalance: 0, newBalance: 0, changePct: 0}
  ]

  return (
    <Dialog
      id="netWorthSnapshotDialog"
      open={true}
      onClose={handleCancel}
      scroll="paper"
      maxWidth="md"
      fullWidth
      aria-labelledby="netWorthSnapshotDialogTitle"
    >
      <Paper sx={{width:"100%"}}>
        <FormValidator onSubmit={handleSave} name="snapshotForm" id="snapshotForm">
          <DialogTitle id="netWorthSnapshotDialogTitle">
            {title}
          </DialogTitle>
          <DialogContent sx={{ my:1, p:1, maxHeight:"calc(100vh - 206px)", overflowY:"scroll"}}>
            <Box display="flex" flexGrow={1} justifyContent="stretch">
              <Box display="flex" flexGrow={1} width="100%">
                <Box display="flex" flexGrow={0} width={40} alignItems="center">
                  <SnapshotIcon style={styles.snapshotIcon}/>
                </Box>
                <Box display="flex" flexGrow={1} pr={2}>
                  <TextFieldValidator
                    margin="none"
                    name="snapshotDescription"
                    label="Description"
                    type="text"
                    fullWidth
                    variant="standard"
                    validators={{ required: false }}
                    value={snapshotDescription}
                    onChange={(event: any) => setSnapshotDescription(event.target.value)}
                  />
                </Box>
                {baseSnap &&
                  <Box display="flex" flexGrow={0} width={130} mr={2}>
                    <DatePickerValidator
                      name="baseDate"
                      label="Prior Date"
                      value={baseSnap.date}
                      format="MM-dd-yyyy"
                      variant="standard"
                      margin="dense"
                      disabled={true}
                    />
                  </Box>
                }
                <Box display="flex" flexGrow={0} width={130} mr={2}>
                  <DatePickerValidator
                    name="snapshotDate"
                    label="New Date"
                    value={snapshotDate}
                    format="MM-dd-yyyy"
                    variant="standard"
                    margin="dense"
                    maxDate={maxDate}
                    required
                    disabled={!canDelete}
                    onChange={(newValue: any) => {
                      // TODO: Validation for valid dates
                      setSnapshotDate(newValue)
                    }}
                  />
                </Box>
                <Box display="flex" flexGrow={0} width={65}></Box>
              </Box>
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Liquid Assets", subtotal)}
              {liquidAssets.map((asset: Asset) => renderItem(asset, subtotal, totals[0], totals[3]))}
              {renderSubtotal(subtotal)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Real Estate", subtotal)}
              {realAssets.map((asset: Asset) => renderItem(asset, subtotal, totals[0], totals[3]))}
              {renderSubtotal(subtotal)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Personal Property", subtotal)}
              {personalAssets.map((asset: Asset) => renderItem(asset, subtotal, totals[0], totals[3]))}
              {renderSubtotal(subtotal)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Other Assets", subtotal)}
              {otherAssets.map((asset: Asset) => renderItem(asset, subtotal, totals[0], totals[3]))}
              {renderSubtotal(subtotal)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column">
              {renderCategory("Liabilities", subtotal)}
              {liabilities.map((liability: Liability) => renderItem(liability, subtotal, totals[1]))}
              {renderSubtotal(subtotal)}
            </Box>
            <Box display="flex" flexGrow={1} flexDirection="column" bgcolor={globalColors.details}>
              {renderCategory("Totals")}
              {renderTotals(totals)}
            </Box>
            <DialogContentText sx={{pt:1}}>
              * Note: If some items are not listed, the acquisition date may be later than the snapshot date.
              {totals[3].count > 0 && " Items marked with * are excluded from Net Worth."}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box mt={1} sx={styles.actions}>
              <Box>
                {onDelete && canDelete &&
                  <IconicButton onClick={handleDelete}>
                    <DeleteIcon/>
                  </IconicButton>
                }
              </Box>
              <Box display="flex" flexWrap="nowrap">
                <DialogButton variant="secondary" type="button" onClick={handleCancel}>
                  Cancel
                </DialogButton>
                <Box width={16}/>
                <DialogButton variant="primary" type="submit">
                  Save
                </DialogButton>
              </Box>
            </Box>
          </DialogActions>
        </FormValidator>
      </Paper>

    </Dialog>
  )

}

export default NetWorthSnapshotDialog